class ProfessionalsMainForm {
  static sheets = new Map();

  static instances = new Map();

  static TRIGGER_ATTRIBUTE = 'data-professionals-form-trigger';

  static OPEN_ATTRIBUTE = 'data-ble-open';

  static delegating = false;

  static styleTag = null;

  static definitions = new Map();

  static loadDefinition(formId) {
    const shop = window.Shopify && window.Shopify.shop;
    if (!shop || !formId) return Promise.resolve(null);

    if (!ProfessionalsMainForm.definitions.has(shop)) {
      const pending = fetch(
        `https://forms.shopifyapps.com/api/v2/form_instances?shopify_domain=${encodeURIComponent(
          shop
        )}`
      )
        .then((response) => {
          if (!response.ok) throw new Error(`form definitions responded ${response.status}`);
          return response.json();
        })
        .then((payload) => {
          const byForm = new Map();

          Object.values(payload || {}).forEach((instance) => {
            const form =
              instance && instance.form_definition ? instance.form_definition.form : null;
            if (!form || !Array.isArray(form.fields)) return;
            byForm.set(
              String(instance.id),
              form.fields.slice().sort((a, b) => a.order - b.order)
            );
          });

          return byForm;
        })
        .catch((error) => {
          /* The promise itself is cached, so a rejected one would poison it. */
          ProfessionalsMainForm.definitions.delete(shop);
          throw error;
        });

      ProfessionalsMainForm.definitions.set(shop, pending);
    }

    return ProfessionalsMainForm.definitions
      .get(shop)
      .then((byForm) => byForm.get(String(formId)) || null);
  }

  /**
   * Dismissing a form writes localStorage["<formId>-dismissed-in-session"], and
   * on every later page load the app then refuses to render that embed at all -
   * the host element is simply absent from the DOM. That is what made a form
   * look permanently broken once anyone had closed it.
   *
   * Clearing the key restores it. This has to run before the app's own loader
   * decides what to render, which it does: our script tag sits above
   * content_for_header, and deferred scripts execute in document order.
   */
  static DISMISS_KEYS = ['-dismissed-in-session', '-dismissed-after-submit'];

  static clearDismissed(formId) {
    if (!formId) return false;
    try {
      ProfessionalsMainForm.DISMISS_KEYS.forEach((suffix) => {
        window.localStorage.removeItem(formId + suffix);
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Show the customer what the server rejected.
   *
   * Submitting posts to /api/v2/form_submission, and when Shopify refuses it
   * answers 422 with a per-field map - {"phone_number": "Phone number is
   * invalid"} - that the app does nothing with. Nothing appears on screen, so
   * the form simply refuses to go through with no way to tell why.
   *
   * Some of these cannot be caught before submitting: the phone number passes
   * the pattern in the form definition and is only rejected by the real check
   * on the server. So the response is the only place the reason exists, and
   * patching fetch is the only way to reach it - the app exposes no hook.
   *
   * The original response is returned untouched; a clone is what gets read.
   */
  static interceptSubmissions() {
    if (ProfessionalsMainForm.submissionsIntercepted) return false;
    ProfessionalsMainForm.submissionsIntercepted = true;

    const original = window.fetch;
    if (typeof original !== 'function') return false;

    window.fetch = function bleFetch(...args) {
      const input = args[0];
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const pending = original.apply(this, args);

      if (!url.includes('/form_submission')) return pending;

      return pending.then((response) => {
        ProfessionalsMainForm.instances.forEach((form) =>
          ProfessionalsMainForm.clearDismissed(form.formId)
        );

        const target =
          Array.from(ProfessionalsMainForm.instances.values()).find((form) => form.visible) || null;

        if (response.ok) {
          if (target) {
            target.captureComplete();
            ProfessionalsMainForm.pushDataLayer('professionals_form_completed', target);
          }
          return response;
        }

        response
          .clone()
          .json()
          .then((body) => {
            if (target) target.showServerErrors(body);
          })
          .catch(() => {
          });

        return response;
      });
    };

    return true;
  }

  static hideUntilTriggered(embedId, formId) {
    const selectors = [];
    if (embedId) selectors.push(`#${embedId}`);
    if (formId) selectors.push(`shopify-forms-embed[id$="${formId}"]`);
    if (!selectors.length) return false;

    if (!ProfessionalsMainForm.styleTag) {
      const tag = document.createElement('style');
      tag.id = 'ble-professionals-form-visibility';
      document.head.appendChild(tag);
      ProfessionalsMainForm.styleTag = tag;
    }

    const open = selectors.map((selector) => `${selector}[${ProfessionalsMainForm.OPEN_ATTRIBUTE}]`);

    ProfessionalsMainForm.styleTag.textContent +=
      `${selectors.join(',')}{display:none!important}` +
      `${open.join(',')}{display:block!important}`;

    return true;
  }

  static delegate() {
    if (ProfessionalsMainForm.delegating) return;
    ProfessionalsMainForm.delegating = true;

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest(`[${ProfessionalsMainForm.TRIGGER_ATTRIBUTE}]`);
      if (!trigger) return;

      const name = trigger.getAttribute(ProfessionalsMainForm.TRIGGER_ATTRIBUTE);
      const form = ProfessionalsMainForm.instances.get(name);
      if (!form) {
        console.warn(
          `[professionals-form] no form registered as "${name}". Registered: ${Array.from(ProfessionalsMainForm.instances.keys()).join(', ') || 'none'
          }`
        );
        return;
      }

      event.preventDefault();
      form.open();
    });
  }

  /* Both scripts load on every page; only boot where the form can open. */
  static shouldBoot({ trigger = null, urlParam = null } = {}) {
    if (window.Shopify && window.Shopify.designMode) return true;

    if (trigger) {
      const selector = `[${ProfessionalsMainForm.TRIGGER_ATTRIBUTE}="${trigger}"]`;
      if (document.querySelector(selector)) return true;
    }

    if (urlParam && new URLSearchParams(window.location.search).has(urlParam)) return true;

    return false;
  }

  constructor({
    embedId,
    formId = null,
    stylesUrl,
    name = 'professionals-form',
    urlParam = null,
    steps = [],
    fullWidthFields = [],
    requiredFields = [],
    stepSubtitles = [],
    requiredNote = '*asterisk indicates field is required.',
    uploadHelp = null,
    fieldNotes = null,
    partialCapture = false,
    startedEvent = null,
    completedEvent = null,
    statusProperty = 'trade_application_status',
    consentStep = 1,
    products = null,
    fieldGroups = [],
    successContent = null,
    headingAccent = null,
    nextLabel = 'Next step',
    backLabel = 'Back',
    tabsLabel = 'Form steps',
    requiredMessage = 'This field is required.',
    emailMessage = 'Enter a valid email address.',
    phoneMessage = 'Enter a valid phone number.',
    minPhoneDigits = 6,
    debug = false,
  }) {
    this.embedId = embedId;
    this.formId = formId;
    this.stylesUrl = stylesUrl;
    this.name = name;
    this.urlParam = urlParam;
    this.steps = steps;
    this.fullWidthFields = fullWidthFields;
    this.requiredFields = requiredFields;
    this.stepSubtitles = stepSubtitles;
    this.requiredNote = requiredNote;
    this.uploadHelp = uploadHelp;
    this.fieldNotes = fieldNotes;
    this.partialCapture = partialCapture;
    this.startedEvent = startedEvent || 'Started Form';
    this.completedEvent = completedEvent || 'Completed Form';
    this.statusProperty = statusProperty;
    this.partialCaptured = false;
    this.consentStep = consentStep;
    this.products = products;
    this.fieldGroups = Array.isArray(fieldGroups) ? fieldGroups : [];
    this.successContent = successContent;
    this.headingAccent = headingAccent;
    this.nextLabel = nextLabel;
    this.backLabel = backLabel;
    this.tabsLabel = tabsLabel;
    this.requiredMessage = requiredMessage;
    this.emailMessage = emailMessage;
    this.phoneMessage = phoneMessage;
    this.minPhoneDigits = minPhoneDigits;
    this.debug = debug;
    this.host = null;
    this.pendingOpen = false;
    this.currentStep = 1;
    this.spec = null;

    ProfessionalsMainForm.clearDismissed(this.formId);
    ProfessionalsMainForm.hideUntilTriggered(this.embedId, this.formId);
    ProfessionalsMainForm.interceptSubmissions();

    ProfessionalsMainForm.instances.set(this.name, this);
    ProfessionalsMainForm.delegate();
  }

  get shadowRoot() {
    return this.host ? this.host.shadowRoot : null;
  }

  get root() {
    return this.shadowRoot ? this.shadowRoot.querySelector('[data-current-step]') : null;
  }

  get step() {
    return this.root ? this.root.getAttribute('data-current-step') : null;
  }

  get isOpen() {
    return this.step === 'form';
  }

  get fields() {
    if (!this.shadowRoot) return [];
    return Array.from(this.shadowRoot.querySelectorAll('[data-testid^="field-"]'));
  }

  get form() {
    return this.shadowRoot ? this.shadowRoot.querySelector('[data-testid="form"]') : null;
  }

  get submitButton() {
    return this.shadowRoot ? this.shadowRoot.querySelector('[data-testid="btn-form-submit"]') : null;
  }

  get stepCount() {
    return this.steps.length;
  }

  opensStep(row, index) {
    const step = this.steps[index - 1];
    if (!step || !step.startsAt) return false;

    const selector = `[data-testid="${step.startsAt}"]`;
    return row.matches(selector) || Boolean(row.querySelector(selector));
  }

  classify(row, step) {
    if (row.matches('[data-testid="btn-form-submit"]')) return 'submit';

    /* Consent is an option, not a field, so it has no field-* testid. */
    const checkbox = row.querySelector('input[type="checkbox"]');
    if (checkbox && !(checkbox.getAttribute('data-testid') || '').startsWith('field-')) {
      return 'always';
    }

    const fillable =
      row.matches('[class*="selectContainer"]') ||
      row.querySelector('input, select, textarea, [class*="selectContainer"]');

    return fillable ? String(step) : 'meta';
  }

  prepareSteps() {
    const form = this.form;
    if (!form || !this.steps.length) return false;
    if (form.dataset.bleReady === 'true') return false;

    let cursor = 1;
    let fieldIndex = 0;

    Array.from(form.children).forEach((row) => {
      while (cursor < this.stepCount && this.opensStep(row, cursor + 1)) cursor += 1;

      const step = this.classify(row, cursor);
      row.setAttribute('data-ble-step', step);

      /* Field rows line up one for one with the definition's fields in order.
         Position, not id - it is the only handle on the rows that carry no
         data-testid, which is most of them: every dropdown, every radio group
         and the upload.

         Consent rows are deliberately excluded. Marketing consent is a form
         option rather than a field, so it has no entry in the definition -
         letting it take an index would shift every field after it onto the
         wrong spec, and with it the required marker and the error wording. */
      if (/^\d+$/.test(step)) {
        row.bleSpec = this.spec ? this.spec[fieldIndex] || null : null;
        fieldIndex += 1;
      }

      const input = row.querySelector('input, select, textarea');
      const testid = input ? input.getAttribute('data-testid') : null;
      if (testid) row.setAttribute('data-ble-field', testid);
      if (
        !testid ||
        step === 'always' ||
        step === 'submit' ||
        this.fullWidthFields.includes(testid)
      ) {
        row.setAttribute('data-ble-span', 'full');
      }

      this.enableFloatingLabels(row);
      this.markRequired(row);
    });

    form.dataset.bleReady = 'true';
    this.renderUploadHelp();
    this.renderFieldNotes();
    this.renderProducts();
    this.renderFieldGroups();
    this.applyHeadingAccent();
    this.renderChrome();
    this.bindStickyHeader();
    this.goToStep(1);
    return true;
  }

  static setControlledValue(input, value) {
    const prototype =
      input instanceof window.HTMLTextAreaElement
        ? window.HTMLTextAreaElement.prototype
        : window.HTMLInputElement.prototype;

    const descriptor = Object.getOwnPropertyDescriptor(prototype, 'value');
    if (descriptor && descriptor.set) descriptor.set.call(input, value);
    else input.value = value;

    input.dispatchEvent(new Event('input', { bubbles: true }));
    return true;
  }

  static normaliseFieldName(name) {
    return String(name || '')
      .toLowerCase()
      .replace(/_+\d+$/, '')
      .replace(/_+$/, '');
  }

  /* Partial submissions ----------------------------------------------------
   *
   * Someone who fills in step 1 and leaves is a lead the trade team never hears
   * about, because nothing reaches Shopify until the final submit. So the
   * moment a valid email is entered we identify the visitor to Klaviyo and fire
   * a "started" event; on a successful submit we fire a "completed" one.
   *
   * Klaviyo's own abandoned-cart shape: the flow triggers on "started" and
   * carries a filter excluding anyone who has since "completed". Because
   * profiles are keyed on email, both events land on the same profile - a
   * partial is not a separate record that has to be merged later.
   *
   * This identifies, and nothing more. It never adds anyone to a list, because
   * subscribing someone who has not ticked the newsletter box is the part that
   * would actually be a consent problem. That box remains the only route to
   * marketing.
   */

  static klaviyo() {
    window.klaviyo = window.klaviyo || [];
    return window.klaviyo;
  }

  static pushDataLayer(event, form) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event,
      form_name: form.name,
      form_id: form.formId,
    });

    if (form.debug) console.log(`[${form.name}] dataLayer ${event}`);
    return true;
  }

  leadValues() {
    const emailRow = this.rowForFieldName('email');
    if (!emailRow || this.errorFor(emailRow)) return null;

    const read = (name) => {
      const row = this.rowForFieldName(name);
      const input = row ? row.querySelector('input, textarea') : null;
      return input ? String(input.value || '').trim() : '';
    };

    const email = read('email');
    if (!email) return null;

    const values = { email };
    const first = read('first_name');
    const last = read('last_name');
    if (first) values.first_name = first;
    if (last) values.last_name = last;

    const phoneRow = this.rowForFieldName('phone_number');
    if (phoneRow && !this.errorFor(phoneRow)) {
      const phone = read('phone_number');
      if (phone) values.phone_number = phone;
    }

    return values;
  }

  sendToKlaviyo(values, status, eventName, eventProperties) {
    const klaviyo = ProfessionalsMainForm.klaviyo();

    const profile = { [this.statusProperty]: status };
    Object.keys(values).forEach((key) => {
      profile[`$${key}`] = values[key];
    });

    klaviyo.push(['identify', profile]);
    klaviyo.push(['track', eventName, eventProperties]);

    if (this.debug) console.log(`[${this.name}] klaviyo ${eventName}`, profile);
    return true;
  }

  capturePartial() {
    if (this.partialCaptured) return false;

    const values = this.leadValues();
    if (!values) return false;

    this.partialCaptured = true;

    ProfessionalsMainForm.pushDataLayer('professionals_form_started', this);

    if (!this.partialCapture) return false;

    this.sendToKlaviyo(values, 'partial', this.startedEvent, {
      form: this.name,
      step: this.currentStep,
    });

    if (this.debug) console.log(`[${this.name}] partial captured`, values);
    return true;
  }

  captureComplete() {
    if (!this.partialCapture) return false;

    const values = this.leadValues();
    if (!values) return false;

    this.sendToKlaviyo(values, 'complete', this.completedEvent, { form: this.name });

    if (this.debug) console.log(`[${this.name}] completion captured`);
    return true;
  }

  get productsRow() {
    if (!this.products || !this.form) return null;

    const wanted = ProfessionalsMainForm.normaliseFieldName(this.products.field);

    return (
      Array.from(this.form.children).find(
        (row) =>
          row.bleSpec && ProfessionalsMainForm.normaliseFieldName(row.bleSpec.name) === wanted
      ) || null
    );
  }

  get productsInput() {
    const row = this.productsRow;
    return row ? row.querySelector('input, textarea') : null;
  }

  renderProducts() {
    if (!this.products) return false;

    const row = this.productsRow;
    if (!row) {
      const available = (this.spec || [])
        .map((field) => field.name)
        .filter((name) => name.startsWith('custom#'));

      console.warn(
        `[${this.name}] products field "${this.products.field}" is not on this form. ` +
        `Form-only fields present: ${available.join(', ') || 'none'}`
      );
      return false;
    }

    if (row.hasAttribute('data-ble-products-host')) return false;
    row.setAttribute('data-ble-products-host', '');

    const block = document.createElement('div');
    block.setAttribute('data-ble-products', '');

    if (this.products.heading) {
      const heading = document.createElement('p');
      heading.setAttribute('data-ble-products-heading', '');
      heading.textContent = this.products.heading;
      block.appendChild(heading);
    }

    if (this.products.intro) {
      const intro = document.createElement('p');
      intro.setAttribute('data-ble-products-intro', '');
      intro.textContent = this.products.intro;
      block.appendChild(intro);
    }

    const list = document.createElement('div');
    list.setAttribute('data-ble-products-list', '');
    block.appendChild(list);

    const add = document.createElement('button');
    add.type = 'button';
    add.setAttribute('data-ble-products-add', '');
    add.textContent = this.products.addLabel || 'Add another product';
    add.addEventListener('click', () => this.addProduct());
    block.appendChild(add);

    row.appendChild(block);

    const stored = this.readProducts();
    const opening = this.products.initial || 1;
    const count = Math.min(Math.max(opening, stored.length), this.products.max || 4);
    for (let index = 0; index < count; index += 1) this.addProduct(stored[index], true);

    this.syncProducts();
    return true;
  }

  readProducts() {
    const input = this.productsInput;
    if (!input || !input.value) return [];

    try {
      const parsed = JSON.parse(input.value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  addProduct(values = null, silent = false) {
    const block = this.productsRow && this.productsRow.querySelector('[data-ble-products-list]');
    if (!block) return false;

    const max = this.products.max || 4;
    if (block.children.length >= max) return false;

    const item = document.createElement('div');
    item.setAttribute('data-ble-product', '');

    const caption = document.createElement('p');
    caption.setAttribute('data-ble-product-caption', '');
    item.appendChild(caption);

    const fields = document.createElement('div');
    fields.setAttribute('data-ble-product-fields', '');

    (this.products.columns || []).forEach((column) => {
      const field = document.createElement('div');
      field.setAttribute('data-ble-product-field', '');
      if (column.width) field.setAttribute('data-ble-product-width', column.width);

      const input = document.createElement('input');
      input.type = column.type || 'text';
      if (input.type === 'number') input.min = '1';
      input.setAttribute('data-ble-product-input', column.key);
      input.placeholder = ' ';
      if (values && values[column.key] != null) input.value = String(values[column.key]);
      input.addEventListener('input', () => this.syncProducts());

      const label = document.createElement('label');
      label.textContent = column.required ? `${column.label}*` : column.label;

      field.append(input, label);
      fields.appendChild(field);
    });

    item.appendChild(fields);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.setAttribute('data-ble-product-remove', '');
    remove.textContent = this.products.removeLabel || 'Remove';
    remove.addEventListener('click', () => {
      item.remove();
      this.syncProducts();
    });
    item.appendChild(remove);

    block.appendChild(item);
    if (!silent) this.syncProducts();
    return true;
  }

  syncProducts() {
    const row = this.productsRow;
    if (!row) return false;

    const items = Array.from(row.querySelectorAll('[data-ble-product]'));
    const max = this.products.max || 4;
    const opening = this.products.initial || 1;

    items.forEach((item, index) => {
      const caption = item.querySelector('[data-ble-product-caption]');
      if (caption) caption.textContent = `Item ${index + 1} of ${items.length}`;
      item.toggleAttribute('data-ble-product-fixed', index < opening);
    });

    const add = row.querySelector('[data-ble-products-add]');
    if (add) add.toggleAttribute('data-ble-hidden', items.length >= max);

    this.serialiseProducts();
    return true;
  }

  collectProducts() {
    const row = this.productsRow;
    if (!row) return [];

    return Array.from(row.querySelectorAll('[data-ble-product]'))
      .map((item) => {
        const values = {};
        Array.from(item.querySelectorAll('[data-ble-product-input]')).forEach((input) => {
          const value = input.value.trim();
          if (value) values[input.getAttribute('data-ble-product-input')] = value;
        });
        return values;
      })
      .filter((values) => Object.keys(values).length);
  }

  serialiseProducts() {
    const input = this.productsInput;
    if (!input) return false;

    const products = this.collectProducts();
    ProfessionalsMainForm.setControlledValue(input, products.length ? JSON.stringify(products) : '');
    return true;
  }

  productsErrorFor(row) {
    const items = Array.from(row.querySelectorAll('[data-ble-product]'));
    const columns = this.products.columns || [];
    const required = columns.filter((column) => column.required).map((column) => column.key);

    let firstProblem = '';

    items.forEach((item, index) => {
      if (firstProblem) return;

      const values = {};
      Array.from(item.querySelectorAll('[data-ble-product-input]')).forEach((input) => {
        input.removeAttribute('data-ble-product-invalid');
        values[input.getAttribute('data-ble-product-input')] = input.value.trim();
      });

      const filled = Object.values(values).some((value) => value);
      const missing = required.filter((key) => !values[key]);

      if (!filled && (index > 0 || !this.products.requireFirst)) return;
      if (!missing.length) return;

      missing.forEach((key) => {
        const input = item.querySelector(`[data-ble-product-input="${key}"]`);
        if (input) input.setAttribute('data-ble-product-invalid', 'true');
      });

      const names = missing
        .map((key) => (columns.find((column) => column.key === key) || {}).label || key)
        .join(', ');

      firstProblem = filled
        ? `Item ${index + 1} is missing: ${names}.`
        : `Add at least one product - ${names} are required.`;
    });

    if (firstProblem) return firstProblem;

    const limit = this.validatorFor(row, 'maxlength');
    const input = this.productsInput;
    const max = limit && Number(String(limit.pattern || limit.max || 1000).replace(/\D/g, ''));

    if (input && max && input.value.length > max) {
      return `Too much product detail to store - please shorten the names or list fewer items.`;
    }

    return '';
  }

  renderFieldGroups() {
    if (!this.fieldGroups.length) return false;
    this.fieldGroups.forEach((group) => this.renderFieldGroup(group));
    return true;
  }

  groupRowFor(group) {
    if (!group || !this.form) return null;

    const wanted = ProfessionalsMainForm.normaliseFieldName(group.field);

    return (
      Array.from(this.form.children).find(
        (row) =>
          row.bleSpec && ProfessionalsMainForm.normaliseFieldName(row.bleSpec.name) === wanted
      ) || null
    );
  }

  groupInputFor(group) {
    const row = this.groupRowFor(group);
    if (!row) return null;

    return (
      Array.from(row.querySelectorAll('input, textarea')).find(
        (control) => !control.hasAttribute('data-ble-group-input')
      ) || null
    );
  }

  renderFieldGroup(group) {
    const row = this.groupRowFor(group);
    if (!row) {
      const available = (this.spec || [])
        .map((field) => field.name)
        .filter((name) => name.startsWith('custom#'));

      console.warn(
        `[${this.name}] grouped field "${group.field}" is not on this form. ` +
        `Form-only fields present: ${available.join(', ') || 'none'}`
      );
      return false;
    }

    if (row.hasAttribute('data-ble-group-host')) return false;
    row.setAttribute('data-ble-group-host', '');
    row.setAttribute('data-ble-span', 'full');

    const block = document.createElement('div');
    block.setAttribute('data-ble-group', '');

    if (group.heading) {
      const heading = document.createElement('p');
      heading.setAttribute('data-ble-group-heading', '');
      heading.textContent = group.heading;
      block.appendChild(heading);
    }

    if (group.intro) {
      const intro = document.createElement('p');
      intro.setAttribute('data-ble-group-intro', '');
      intro.textContent = group.intro;
      block.appendChild(intro);
    }

    const fields = document.createElement('div');
    fields.setAttribute('data-ble-group-fields', '');

    const stored = this.readGroup(group);

    (group.columns || []).forEach((column) => {
      const field = document.createElement('div');
      field.setAttribute('data-ble-group-field', '');
      if (column.width) field.setAttribute('data-ble-group-width', column.width);

      const control = document.createElement('input');
      control.type = column.type || 'text';
      control.setAttribute('data-ble-group-input', column.key);
      control.placeholder = ' ';
      if (stored[column.key] != null) control.value = String(stored[column.key]);
      control.addEventListener('input', () => this.serialiseGroup(group));

      const label = document.createElement('label');
      label.textContent = column.required ? `${column.label}*` : column.label;

      field.append(control, label);
      fields.appendChild(field);
    });

    block.appendChild(fields);
    row.appendChild(block);

    this.serialiseGroup(group);
    return true;
  }

  readGroup(group) {
    const input = this.groupInputFor(group);
    if (!input || !input.value) return {};

    try {
      const parsed = JSON.parse(input.value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  serialiseGroup(group) {
    const row = this.groupRowFor(group);
    const input = this.groupInputFor(group);
    if (!row || !input) return false;

    const values = {};
    Array.from(row.querySelectorAll('[data-ble-group-input]')).forEach((control) => {
      const value = control.value.trim();
      if (value) values[control.getAttribute('data-ble-group-input')] = value;
    });

    ProfessionalsMainForm.setControlledValue(
      input,
      Object.keys(values).length ? JSON.stringify(values) : ''
    );
    return true;
  }

  groupErrorFor(row) {
    const group = this.fieldGroups.find((candidate) => this.groupRowFor(candidate) === row);
    if (!group) return '';

    let firstProblem = '';

    (group.columns || []).forEach((column) => {
      const control = row.querySelector(`[data-ble-group-input="${column.key}"]`);
      if (!control) return;

      control.removeAttribute('data-ble-group-invalid');
      if (!column.required || control.value.trim()) return;

      control.setAttribute('data-ble-group-invalid', '');
      if (!firstProblem) firstProblem = `${column.label} is required.`;
    });

    if (firstProblem) return firstProblem;

    const limit = this.validatorFor(row, 'maxlength');
    const input = this.groupInputFor(group);
    const max = limit && Number(String(limit.pattern || limit.max || 1000).replace(/\D/g, ''));

    if (input && max && input.value.length > max) {
      return 'Too much detail to store - please shorten your answer.';
    }

    return '';
  }

  renderFieldNotes() {
    if (!this.fieldNotes) return 0;

    let added = 0;

    Object.keys(this.fieldNotes).forEach((name) => {
      const row = this.rowForFieldName(name);
      if (!row || row.querySelector('[data-ble-field-note]')) return;

      const note = document.createElement('p');
      note.setAttribute('data-ble-field-note', '');
      note.textContent = this.fieldNotes[name];
      row.appendChild(note);
      added += 1;
    });

    return added;
  }

  renderUploadHelp() {
    const form = this.form;
    if (!this.uploadHelp || !form) return false;

    const row = Array.from(form.children).find((child) =>
      child.querySelector('input[type="file"]')
    );
    if (!row || row.querySelector('[data-ble-help]')) return false;

    const help = document.createElement('div');
    help.setAttribute('data-ble-help', '');

    const items = this.uploadHelp.items || [];
    if (items.length) {
      const list = document.createElement('ul');
      items.forEach((text) => {
        const item = document.createElement('li');
        item.textContent = text;
        list.appendChild(item);
      });
      help.appendChild(list);
    }

    if (this.uploadHelp.footnote) {
      const note = document.createElement('p');
      note.setAttribute('data-ble-help-note', '');
      note.textContent = this.uploadHelp.footnote;
      help.appendChild(note);
    }

    const label = row.querySelector(':scope > p');
    if (label) label.insertAdjacentElement('afterend', help);
    else row.insertBefore(help, row.firstChild);

    return true;
  }

  renderSuccess() {
    const content = this.successContent;
    if (!content || !this.shadowRoot) return false;

    const banner = this.shadowRoot.querySelector('[data-testid="banner-success"]');
    if (!banner || banner.dataset.bleSuccess === 'true') return false;

    banner.dataset.bleSuccess = 'true';
    banner.textContent = '';

    if (content.heading) {
      const heading = document.createElement('h2');
      heading.setAttribute('data-ble-success-heading', '');
      heading.textContent = content.heading;
      banner.appendChild(heading);
    }

    const intro = Array.isArray(content.intro) ? content.intro : content.intro ? [content.intro] : [];
    if (intro.length) {
      const wrap = document.createElement('div');
      wrap.setAttribute('data-ble-success-intro', '');
      intro.forEach((line) => {
        const paragraph = document.createElement('p');
        paragraph.textContent = line;
        wrap.appendChild(paragraph);
      });
      banner.appendChild(wrap);
    }

    const steps = content.steps || [];
    if (steps.length) {
      const list = document.createElement('ol');
      list.setAttribute('data-ble-success-steps', '');

      steps.forEach((step, index) => {
        const item = document.createElement('li');

        const number = document.createElement('span');
        number.setAttribute('data-ble-success-num', '');
        number.setAttribute('aria-hidden', 'true');
        number.textContent = String(index + 1);
        item.appendChild(number);

        const body = document.createElement('div');
        if (step.title) {
          const title = document.createElement('p');
          title.setAttribute('data-ble-success-step-title', '');
          title.textContent = step.title;
          body.appendChild(title);
        }
        if (step.text) {
          const text = document.createElement('p');
          text.setAttribute('data-ble-success-step-text', '');
          text.textContent = step.text;
          body.appendChild(text);
        }
        item.appendChild(body);
        list.appendChild(item);
      });

      banner.appendChild(list);
    }

    if (content.contact) {
      const divider = document.createElement('hr');
      divider.setAttribute('data-ble-success-divider', '');
      banner.appendChild(divider);

      const contact = document.createElement('p');
      contact.setAttribute('data-ble-success-contact', '');
      if (content.contact.label) contact.append(`${content.contact.label} `);
      if (content.contact.email) {
        const link = document.createElement('a');
        link.href = `mailto:${content.contact.email}`;
        link.textContent = content.contact.email;
        contact.appendChild(link);
      }
      banner.appendChild(contact);
    }

    if (content.cta && content.cta.label) {
      const cta = document.createElement('a');
      cta.setAttribute('data-ble-success-cta', '');
      cta.href = content.cta.href || '/collections/all';
      cta.textContent = content.cta.label;
      banner.appendChild(cta);
    }

    if (this.debug) console.log(`[${this.name}] success rendered`);
    return true;
  }

  enableFloatingLabels(row) {
    const inputs = row.querySelectorAll(
      'input:not([type="checkbox"]):not([type="radio"]):not([type="file"]), textarea'
    );

    Array.from(inputs).forEach((input) => {
      if (!input.placeholder) input.placeholder = ' ';
    });

    return inputs.length;
  }

  applyHeadingAccent() {
    if (!this.headingAccent || !this.shadowRoot) return false;

    const heading = this.shadowRoot.querySelector('[class*="textHeading"]');
    if (!heading || heading.dataset.bleAccent === 'true') return false;

    const text = heading.textContent;
    const at = text.lastIndexOf(this.headingAccent);
    if (at === -1) return false;

    const accent = document.createElement('em');
    accent.textContent = this.headingAccent;

    heading.textContent = '';
    heading.append(text.slice(0, at), accent, text.slice(at + this.headingAccent.length));
    heading.dataset.bleAccent = 'true';
    return true;
  }

  bindStickyHeader() {
    const tabs = this.shadowRoot && this.shadowRoot.querySelector('[data-ble-tabs]');
    const scroller = this.form && this.form.closest('[class*="formContainer"]');
    if (!tabs || !scroller) return false;

    if (this.stickyTabs === tabs && this.stickyScroller === scroller) return false;

    this.stickyTabsHeight = 0;

    const header = tabs.parentElement.querySelector('[class*="formHeader"]');

    const measure = () => {
      const h = tabs.offsetHeight;
      if (Math.abs(h - (this.stickyTabsHeight || 0)) >= 2) {
        this.stickyTabsHeight = h;
        scroller.style.setProperty('--ble-sticky-offset', `${h}px`);
      }
      const pinned = `${h + (header ? header.offsetHeight : 0) + 2}px`;
      if (scroller.style.scrollPaddingTop !== pinned) {
        scroller.style.scrollPaddingTop = pinned;
      }
    };
    this.stickyMeasure = measure;

    if (this.stickyObserver) this.stickyObserver.disconnect();
    this.stickyObserver = new ResizeObserver(measure);
    this.stickyObserver.observe(tabs);
    if (header) this.stickyObserver.observe(header);
    this.stickyTabs = tabs;
    measure();

    this.stickyScroller = scroller;

    return true;
  }

  renderChrome() {
    const form = this.form;
    const header = form.parentElement;
    if (!header) return;

    if (!header.querySelector('[data-ble-tabs]')) {
      const tabs = document.createElement('nav');
      tabs.setAttribute('data-ble-tabs', '');
      tabs.setAttribute('aria-label', this.tabsLabel);

      this.steps.forEach((step, index) => {
        const tab = document.createElement('button');
        tab.type = 'button';
        tab.setAttribute('data-ble-tab', String(index + 1));
        tab.textContent = `${index + 1}. ${step.label}`;
        tabs.appendChild(tab);
      });

      tabs.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-ble-tab]');
        if (!tab) return;
        const target = Number(tab.getAttribute('data-ble-tab'));
        if (target < this.currentStep) this.goToStep(target);
      });

      header.insertBefore(tabs, header.firstChild);
    }

    if (this.requiredNote) {
      const formHeader = header.querySelector('[class*="formHeader"]');

      if (formHeader && !formHeader.querySelector('[data-ble-note]')) {
        const note = document.createElement('p');
        note.setAttribute('data-ble-note', '');
        note.textContent = this.requiredNote;
        formHeader.appendChild(note);

        formHeader.setAttribute('data-ble-has-note', '');
      }
    }

    if (this.stepSubtitles.length) {
      const formHeader = header.querySelector('[class*="formHeader"]');
      const heading = formHeader && formHeader.querySelector('[class*="textHeading"]');

      if (heading && !formHeader.querySelector('[data-ble-subtitle]')) {
        const subtitle = document.createElement('p');
        subtitle.setAttribute('data-ble-subtitle', '');
        heading.insertAdjacentElement('afterend', subtitle);
      }
    }

    if (!form.querySelector('[data-ble-next]')) {
      const next = document.createElement('button');
      next.type = 'button';
      next.setAttribute('data-ble-next', '');
      next.setAttribute('data-ble-span', 'full');
      next.textContent = this.nextLabel;
      next.addEventListener('click', () => this.advance());
      form.insertBefore(next, this.submitButton);
    }

    if (form.dataset.bleValidationBound !== 'true') {
      const revalidate = (event) => {
        const row = event.target.closest('[data-ble-invalid]');
        if (!row) return;

        row.removeAttribute('data-ble-server-error');

        if (!this.errorFor(row)) this.clearError(row);
      };
      form.addEventListener('input', revalidate);
      form.addEventListener('change', revalidate);

      let pending = null;
      form.addEventListener('input', () => {
        window.clearTimeout(pending);
        pending = window.setTimeout(() => this.capturePartial(), 800);
      });

      form.dataset.bleValidationBound = 'true';
    }

    if (form.dataset.bleSubmitBound !== 'true') {
      form.addEventListener(
        'submit',
        (event) => {
          const failures = this.validateAll();

          for (let step = 1; step <= this.stepCount; step += 1) {
            this.rowsForStep(step).forEach((row) => {
              row.removeAttribute('data-ble-server-error');
              this.clearError(row);
            });
          }
          this.clearFormError();

          if (!failures.length) return;

          event.preventDefault();
          event.stopImmediatePropagation();

          failures.forEach(({ row, message }) => this.showError(row, message));

          const first = failures[0];
          if (first.step !== this.currentStep) this.goToStep(first.step);

          const input = first.row.querySelector('input, select, textarea');
          if (input) input.focus();
        },
        true
      );
      form.dataset.bleSubmitBound = 'true';
    }

    if (!form.querySelector('[data-ble-back]')) {
      const back = document.createElement('button');
      back.type = 'button';
      back.setAttribute('data-ble-back', '');
      back.textContent = this.backLabel;
      back.addEventListener('click', () => this.goToStep(this.currentStep - 1));
      form.insertBefore(back, form.querySelector('[data-ble-next]') || this.submitButton);
    }
  }

  isRequired(row) {
    if (row.bleSpec) return Boolean(this.validatorFor(row, 'required'));

    const input = row.querySelector('input, select, textarea');
    const testid = input ? input.getAttribute('data-testid') : null;
    if (testid && this.requiredFields.includes(testid)) return true;

    const title = this.titleFor(row);
    return Boolean(title) && title.textContent.trim().endsWith('*');
  }

  validatorFor(row, name) {
    const spec = row.bleSpec;
    if (!spec || !Array.isArray(spec.validators)) return null;
    return spec.validators.find((validator) => validator.name === name) || null;
  }

  messageFor(row, name, fallback) {
    const validator = this.validatorFor(row, name);
    const message = (validator && validator.message) || fallback;

    if (message === 'Field is required') {
      const label = this.labelFor(row);
      if (label) return `${label} is required`;
    }

    return message;
  }

  labelFor(row) {
    const spec = row.bleSpec;
    const text = spec && spec.label ? spec.label : '';
    return text.replace(/\s*\*\s*$/, '').trim();
  }

  titleFor(row) {
    return (
      row.querySelector('label[class*="formInputFieldLabel"]') ||
      row.querySelector(':scope > legend') ||
      row.querySelector(':scope > p')
    );
  }

  markRequired(row) {
    const title = this.titleFor(row);
    if (!title || title.querySelector('[data-ble-required]')) return false;
    if (!this.isRequired(row)) return false;

    this.stripTypedAsterisk(title);

    const marker = document.createElement('span');
    marker.setAttribute('data-ble-required', '');
    marker.textContent = '*';
    title.appendChild(marker);
    return true;
  }

  stripTypedAsterisk(title) {
    const walker = document.createTreeWalker(title, NodeFilter.SHOW_TEXT);
    let last = null;

    while (walker.nextNode()) {
      if (walker.currentNode.textContent.trim()) last = walker.currentNode;
    }

    if (!last) return false;
    last.textContent = last.textContent.replace(/\s*\*\s*$/, '');
    return true;
  }

  showServerErrors(body) {
    const errors = (body && body.errors) || {};
    const names = Object.keys(errors);
    const unplaceable = [];
    let firstStep = null;

    names.forEach((name) => {
      const row = this.rowForFieldName(name);
      if (!row) {
        unplaceable.push(errors[name]);
        return;
      }

      this.showError(row, errors[name]);
      row.setAttribute('data-ble-server-error', 'true');

      const step = Number(row.getAttribute('data-ble-step'));
      if (Number.isFinite(step) && (firstStep === null || step < firstStep)) firstStep = step;
    });

    const summary = unplaceable.length ? unplaceable.join(' ') : names.length ? '' : body.message;
    if (summary) this.showFormError(summary);
    else this.clearFormError();

    if (firstStep !== null && firstStep !== this.currentStep) this.goToStep(firstStep);

    const first = names.length ? this.rowForFieldName(names[0]) : null;
    const focus = first && first.querySelector('input, select, textarea');
    if (focus) focus.focus();

    return names.length;
  }

  valueFor(row) {
    const spec = row.bleSpec;

    if (!spec || spec.type !== 'date') {
      const input = row.querySelector('input, select, textarea');
      return input ? String(input.value || '').trim() : '';
    }

    const parts = Array.from(row.querySelectorAll('input')).map((box) => box.value.trim());
    if (!parts.length || parts.every((part) => !part)) return '';
    if (parts.some((part) => !part)) return null;

    return parts.join('/');
  }

  rowForFieldName(name) {
    const form = this.form;
    if (!form) return null;
    return (
      Array.from(form.children).find((row) => row.bleSpec && row.bleSpec.name === name) || null
    );
  }

  showFormError(message) {
    const form = this.form;
    if (!form) return false;

    let note = form.querySelector('[data-ble-form-error]');
    if (!note) {
      note = document.createElement('p');
      note.setAttribute('data-ble-form-error', '');
      note.setAttribute('role', 'alert');
      form.insertBefore(note, form.querySelector('[data-ble-back]') || this.submitButton);
    }

    note.textContent = message;
    return true;
  }

  clearFormError() {
    const note = this.form && this.form.querySelector('[data-ble-form-error]');
    if (note) note.remove();
    return true;
  }

  rowsForStep(step) {
    const form = this.form;
    if (!form) return [];
    return Array.from(form.children).filter(
      (row) => row.getAttribute('data-ble-step') === String(step)
    );
  }

  errorFor(row) {
    if (row.hasAttribute('data-ble-products-host')) return this.productsErrorFor(row);
    if (row.hasAttribute('data-ble-group-host')) return this.groupErrorFor(row);

    const required = this.isRequired(row);
    const missing = this.messageFor(row, 'required', this.requiredMessage);

    const toggle = row.querySelector(':scope > button[class*="selectToggle"]');
    if (toggle) {
      if (!required) return '';
      const chosen = toggle.querySelector('[class*="selectToggleText"]');
      return chosen && chosen.textContent.trim() ? '' : missing;
    }

    const group = row.querySelectorAll('input[type="radio"], input[type="checkbox"]');
    if (group.length) {
      if (!required) return '';
      return Array.from(group).some((box) => box.checked) ? '' : missing;
    }

    const input = row.querySelector('input, select, textarea');
    if (!input) return '';

    const value = this.valueFor(row);

    if (value === null) {
      const format = (row.bleSpec && row.bleSpec.format) || 'MM/DD/YYYY';
      return `Enter the full date as ${format}.`;
    }

    if (!value) return required ? missing : '';

    const pattern = this.validatorFor(row, 'pattern');
    if (pattern && pattern.pattern) {
      let regex = null;
      try {
        regex = new RegExp(pattern.pattern);
      } catch (error) {
        regex = null;
      }
      if (regex && !regex.test(value)) return pattern.message || this.requiredMessage;
    }

    const testid = input.getAttribute('data-testid') || '';
    const isEmail = input.type === 'email' || testid.includes('email');

    if (!pattern && isEmail) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value) ? '' : this.emailMessage;
    }

    if (isEmail && !/\.[a-zA-Z]{2,}$/.test(value)) {
      return this.messageFor(row, 'pattern', this.emailMessage);
    }

    if (input.type === 'tel' || testid.includes('phone')) {
      if (value.replace(/\D/g, '').length < this.minPhoneDigits) {
        return this.messageFor(row, 'pattern', this.phoneMessage);
      }
    }

    return '';
  }

  validateStep(step) {
    return this.rowsForStep(step)
      .map((row) => ({ row, message: this.errorFor(row) }))
      .filter((result) => result.message);
  }

  validateAll() {
    const failures = [];
    for (let step = 1; step <= this.stepCount; step += 1) {
      this.validateStep(step).forEach((failure) => failures.push({ ...failure, step }));
    }
    return failures;
  }

  showError(row, message) {
    row.setAttribute('data-ble-invalid', 'true');

    const input = row.querySelector('input, select, textarea');
    if (input) input.setAttribute('aria-invalid', 'true');

    let note = row.querySelector('[data-ble-error]');
    if (!note) {
      note = document.createElement('span');
      note.setAttribute('data-ble-error', '');
      note.setAttribute('role', 'alert');
      row.appendChild(note);
    }
    note.textContent = message;

    /* role="alert" only announces on insert; describedby is what gets read
       when focus comes back to the field. */
    if (!input) return;

    if (!note.id) {
      const base = input.id || input.getAttribute('data-testid') || 'field';
      note.id = `ble-error-${this.formId || this.name}-${base}`;
    }

    const described = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
    if (!described.includes(note.id)) {
      described.push(note.id);
      input.setAttribute('aria-describedby', described.join(' '));
    }
  }

  clearError(row) {
    row.removeAttribute('data-ble-invalid');

    const input = row.querySelector('input, select, textarea');
    if (input) input.setAttribute('aria-invalid', 'false');

    const note = row.querySelector('[data-ble-error]');
    if (!note) return;

    if (input && note.id) {
      const rest = (input.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter((id) => id && id !== note.id);
      if (rest.length) input.setAttribute('aria-describedby', rest.join(' '));
      else input.removeAttribute('aria-describedby');
    }

    note.remove();
  }

  advance() {
    const failures = this.validateStep(this.currentStep);
    this.rowsForStep(this.currentStep).forEach((row) => this.clearError(row));

    if (failures.length) {
      failures.forEach(({ row, message }) => this.showError(row, message));

      const first = failures[0].row.querySelector('input, select, textarea');
      if (first) first.focus();
      return false;
    }

    return this.goToStep(this.currentStep + 1);
  }

  goToStep(step) {
    const form = this.form;
    if (!form) return false;

    const target = Math.min(Math.max(step, 1), this.stepCount);
    this.currentStep = target;

    if (this.stickyMeasure) this.stickyMeasure();

    form.setAttribute('data-ble-current-step', String(target));
    form.setAttribute('data-ble-step-count', String(this.stepCount));
    form.toggleAttribute('data-ble-last-step', target === this.stepCount);
    form.toggleAttribute('data-ble-with-back', target > 1);

    Array.from(form.children).forEach((row) => {
      const kind = row.getAttribute('data-ble-step');
      let hidden = false;

      if (/^\d+$/.test(kind || '')) hidden = Number(kind) !== target;
      else if (kind === 'submit') hidden = target !== this.stepCount;
      else if (kind === 'always') hidden = this.consentStep ? target !== this.consentStep : false;

      row.toggleAttribute('data-ble-hidden', hidden);
    });

    const next = form.querySelector('[data-ble-next]');
    const back = form.querySelector('[data-ble-back]');
    if (next) next.toggleAttribute('data-ble-hidden', target === this.stepCount);
    if (back) back.toggleAttribute('data-ble-hidden', target === 1);

    Array.from(form.parentElement.querySelectorAll('[data-ble-tab]')).forEach((tab) => {
      const index = Number(tab.getAttribute('data-ble-tab'));
      tab.setAttribute('aria-current', index === target ? 'step' : 'false');
      tab.disabled = index > target;
    });

    const subtitle = form.parentElement.querySelector('[data-ble-subtitle]');
    if (subtitle) {
      const text = this.stepSubtitles[target - 1] || '';
      subtitle.textContent = text;
      subtitle.hidden = text === '';
    }

    return true;
  }

  init() {
    return this.waitForHost()
      .then((host) => {
        this.host = host;
        return Promise.all([
          this.applyStyles().catch((error) =>
            console.warn(`[${this.name}] styles: ${error.message}`)
          ),
          ProfessionalsMainForm.loadDefinition(this.formId)
            .then((fields) => {
              this.spec = fields;
            })
            .catch((error) => console.warn(`[${this.name}] definition: ${error.message}`)),
        ]);
      })
      .then(() => this.onReady())
      .catch((error) => console.warn(`[${this.name}] ${error.message}`));
  }

  findHost() {
    if (this.embedId) {
      const exact = document.getElementById(this.embedId);
      if (exact && exact.shadowRoot) return exact;
    }

    const embeds = Array.from(document.querySelectorAll('shopify-forms-embed')).filter(
      (embed) => embed.shadowRoot
    );

    if (this.formId) {
      const byFormId = embeds.find((embed) => embed.id.endsWith(String(this.formId)));
      if (byFormId) return byFormId;
    }

    /* Both forms boot everywhere, so never take an embed another one owns. */
    if (embeds.length !== 1) return null;

    const candidate = embeds[0];
    const claimedByAnother = Array.from(ProfessionalsMainForm.instances.values()).some(
      (form) =>
        form !== this &&
        (form.host === candidate ||
          (form.formId && candidate.id.endsWith(String(form.formId))))
    );

    return claimedByAnother ? null : candidate;
  }

  waitForHost({ timeout = 15000, interval = 100 } = {}) {
    return new Promise((resolve, reject) => {
      const startedAt = Date.now();
      const tick = () => {
        const host = this.findHost();
        if (host) {
          resolve(host);
          return;
        }
        if (Date.now() - startedAt > timeout) {
          const found = Array.from(document.querySelectorAll('shopify-forms-embed')).map(
            (embed) => embed.id || '(no id)'
          );
          reject(
            new Error(
              `embed "${this.embedId}" never appeared. shopify-forms-embed elements on this page: ${found.length ? found.join(', ') : 'none'
              }. If none, the Forms app is not placing this form here - check the form's placement and page targeting.`
            )
          );
          return;
        }
        window.setTimeout(tick, interval);
      };
      tick();
    });
  }

  loadSheet() {
    if (!this.stylesUrl) return Promise.reject(new Error('no stylesUrl given'));

    if (!ProfessionalsMainForm.sheets.has(this.stylesUrl)) {
      const pending = fetch(this.stylesUrl)
        .then((response) => {
          if (!response.ok) throw new Error(`stylesheet responded ${response.status}`);
          return response.text();
        })
        .then((css) => {
          const sheet = new CSSStyleSheet();
          sheet.replaceSync(css);
          return sheet;
        });

      ProfessionalsMainForm.sheets.set(this.stylesUrl, pending);
    }

    return ProfessionalsMainForm.sheets.get(this.stylesUrl);
  }

  applyStyles() {
    return this.loadSheet().then((sheet) => this.adopt(sheet));
  }

  adopt(sheet) {
    const shadow = this.shadowRoot;
    if (!shadow || shadow.adoptedStyleSheets.includes(sheet)) return false;

    shadow.adoptedStyleSheets = shadow.adoptedStyleSheets.concat(sheet);
    return true;
  }

  hide() {
    if (this.host) this.host.removeAttribute(ProfessionalsMainForm.OPEN_ATTRIBUTE);
  }

  show() {
    if (this.host) this.host.setAttribute(ProfessionalsMainForm.OPEN_ATTRIBUTE, '');
    if (this.stickyMeasure) this.stickyMeasure();
  }

  get visible() {
    return Boolean(this.host) && this.host.hasAttribute(ProfessionalsMainForm.OPEN_ATTRIBUTE);
  }

  open() {
    if (!this.host) {
      this.pendingOpen = true;
      return false;
    }

    ProfessionalsMainForm.instances.forEach((instance) => {
      if (instance !== this) instance.hide();
    });

    this.show();

    const teaser = this.shadowRoot && this.shadowRoot.querySelector('[data-testid="form-teaser"]');
    if (!this.isOpen && teaser) teaser.click();

    return true;
  }

  diagnose() {
    const shadow = this.shadowRoot;
    return {
      embedId: this.embedId,
      hostFound: Boolean(this.host),
      visible: this.visible,
      shadowRootOpen: Boolean(shadow),
      step: this.step,
      teaserPresent: shadow ? Boolean(shadow.querySelector('[data-testid="form-teaser"]')) : null,
      formPresent: shadow ? Boolean(shadow.querySelector('[data-testid="form"]')) : null,
      stylesheetAdopted: shadow ? shadow.adoptedStyleSheets.length : null,
      triggersOnPage: document.querySelectorAll(
        `[${ProfessionalsMainForm.TRIGGER_ATTRIBUTE}="${this.name}"]`
      ).length,
    };
  }

  openFromUrl() {
    if (!this.urlParam) return false;
    if (!new URLSearchParams(window.location.search).has(this.urlParam)) return false;
    return this.open();
  }

  close() {
    this.hide();
    return true;
  }

  watch() {
    if (this.observer || !this.shadowRoot) return;

    this.observer = new MutationObserver(() => {
      if (this.watchQueued) return;
      this.watchQueued = true;
      requestAnimationFrame(() => {
        this.watchQueued = false;
        this.prepareSteps();
        this.clearFixedRows();
        this.renderSuccess();
      });
    });

    this.observer.observe(this.shadowRoot, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    this.prepareSteps();
    this.renderSuccess();
    this.interceptClose();
  }

  clearFixedRows() {
    const form = this.form;
    if (!form) return 0;

    const fixed = Array.from(form.querySelectorAll('[data-ble-invalid]')).filter(
      (row) => !row.hasAttribute('data-ble-server-error') && !this.errorFor(row)
    );

    fixed.forEach((row) => this.clearError(row));
    return fixed.length;
  }

  interceptClose() {
    if (!this.shadowRoot || this.closeIntercepted) return;
    this.closeIntercepted = true;

    this.shadowRoot.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!target || !target.closest) return;

        const onCloseButton = Boolean(target.closest('[class*="formCloseButton"]'));

        const onBackdrop =
          Boolean(target.closest('[class*="overlay"]')) &&
          !target.closest('[class*="formContainer"]');

        if (!onCloseButton && !onBackdrop) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.close();
      },
      true
    );

    this.onEscape = (event) => {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      if (!this.visible) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.close();
    };

    window.addEventListener('keydown', this.onEscape, true);
  }

  onReady() {
    this.watch();

    if (this.pendingOpen) {
      this.pendingOpen = false;
      this.open();
    } else {
      this.openFromUrl();
    }

    if (!this.debug) return;
    console.log(`[${this.name}] ready`, {
      step: this.step,
      fields: this.fields.map((field) => field.getAttribute('data-testid')),
      sheets: this.shadowRoot.adoptedStyleSheets.length,
    });
  }
}

window.ProfessionalsMainForm = ProfessionalsMainForm;
